import { useCallback, useMemo, useState } from 'react';
import { BasicUserInfo } from 'models/User';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { DEBOUNCE_TIME } from 'data/global';
import {
  debounce,
  getArrayLength,
  getHasElementsInArray,
  isEmail,
} from 'utils';
import api from 'api';

type PrefillUserBasicInfo = {
  email: string;
  form: { batch: any; change: any };
  fieldsToPrefill?: (keyof BasicUserInfo)[];
};

type PrefilledFields = Partial<Record<keyof BasicUserInfo, boolean>>;

const DEFAULT_FIELDS = ['firstName', 'lastName'] as (keyof BasicUserInfo)[];

export default function useUserBasicInfo() {
  const [isFetchingUserBasicInfo, setIsFetchingUserBasicInfo] = useState(false);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledFields>({});

  const getMixpanelProps = useGetMixpanelProps();

  const isUserBasicInfoSet = useMemo(
    () => getHasElementsInArray(Object.values(prefilledFields)),
    [prefilledFields],
  );

  const resetState = useCallback(() => {
    setIsFetchingUserBasicInfo(false);
    setPrefilledFields({});
  }, []);

  const prefillUserBasicInfo = useCallback(
    async ({
      email,
      form: { batch, change },
      fieldsToPrefill = DEFAULT_FIELDS,
    }: PrefillUserBasicInfo) => {
      if (!isEmail(email)) {
        resetState();
        return;
      }

      try {
        setIsFetchingUserBasicInfo(true);

        const { data } = await api.users.getUserBasicInfo(email);

        const userInfo = data?.user || ({} as BasicUserInfo);

        const existingUserFields = Object.fromEntries(
          Object.entries(userInfo).filter(([_, value]) => !!value),
        );

        const hasUserInfo = getArrayLength(Object.keys(existingUserFields));

        if (!hasUserInfo) {
          resetState();
          return;
        }

        const prefilledFields: PrefilledFields = {};

        batch(() =>
          fieldsToPrefill.forEach((field) => {
            const value = existingUserFields[field];

            if (value) {
              prefilledFields[field] = true;
              change(field, value);
            }
          }),
        );

        setIsFetchingUserBasicInfo(false);
        setPrefilledFields(prefilledFields);
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
        resetState();
      }
    },
    [getMixpanelProps, resetState],
  );

  const onEmailChange: (args: PrefillUserBasicInfo) => void = debounce(
    async (args: PrefillUserBasicInfo) => {
      await prefillUserBasicInfo(args);
    },
    DEBOUNCE_TIME,
  );

  return {
    isFetchingUserBasicInfo,
    isUserBasicInfoSet,
    prefilledFields,
    prefillUserBasicInfo,
    resetState,
    onEmailChange,
  };
}
